/**
 * @author Maria Osama
 * @copyright Copyright 2020 by Radivision Inc., CA, USA. All Rights Reserved.
 * @Date: 2018-08-08
 * @description Implementation of the Footer controller.
 * @filename footer-controller.tsx
 */
import React from "react";
import { RelayProps } from "../../relay/relay-props";
import { Router } from "../../utilities/router";
import { AboutUs } from "./company-nav/about-us";
import { PrivacyPolicy } from "./legal-nav/privacy-policy";
import { TermsAndConditions } from "./legal-nav/terms-and-conditions";
import { ContactUs } from "./support-nav/contact-us";
import { FAQS } from "./support-nav/faqs";
import { ReportBug } from "./support-nav/report-bug";
import { TechnicalIssues } from "./support-nav/technical-issues";

/**
 * The props of the Footer component.
 *
 * @export
 * @interface
 */
interface FooterControllerProps extends RelayProps {
  /**
   * The name of the footer section.
   *
   * @type {string}
   */
  sectionName: string;

  /**
   * The tabs of the footer.
   *
   * @type {Array<string>}
   */
  tabs: string[];

  /**
   * The state of the tabs of the footer.
   *
   * @type {Array<string>}
   */
  tabState: string[];

  /**
   *
   *
   * @type {string}
   * @memberof FooterControllerProps
   */
  activeTab: string;

  /**
   *
   *
   * @type {string}
   * @memberof FooterControllerProps
   */
  location?: string;
}

/**
 * The state of the Footer component.
 *
 * @export
 * @interface
 */
interface FooterControllerState {
  /**
   * the active tab of in the section.
   *
   * @type {string}
   * @memberof FooterControllerProps
   */
  activeTab: string;

  /**
   * The state of the tab(Active/not).
   *
   * @type {Array<string>}
   */
  tabState: string[];
}

/**
 *  A React component that renders the footer.
 *
 * @export
 * @class Navbar
 * @extends {React.Component<FooterControllerProps, FooterControllerState>}
 */
export class FooterController extends React.Component<
  FooterControllerProps,
  FooterControllerState
> {
  /**
   * Constructor.
   *
   * @param {FooterControllerProps} props The props of the component.
   */
  constructor(props: FooterControllerProps) {
    super(props);
    this.onHashChange = this.onHashChange.bind(this);
    this.state = {
      activeTab: this.props.activeTab,
      tabState: this.props.tabState,
    };
  }

  /**
   * Updates the current tab and render it's content
   *
   * @param {FooterControllerProps} props The props of the component.
   *
   * @param {*} state The state of the component.
   *
   * @returns {(void|FooterControllerState)} The updated state.
   */
  static getDerivedStateFromProps(
    props: FooterControllerProps,
    state: FooterControllerState
  ): Promise<FooterControllerState | void> {
    return Router.getLocation().then((location: string) => {
      const currentTab: string = location;
      let activeTab: string = props.activeTab;
      let tabsState: string[] = props.tabState;

      for (const i in props.tabs) {
        if (
          props.tabs[i]
            .toLocaleLowerCase()
            .replace(/\s*\&+\s*/g, "-")
            .replace(/\s+/g, "-")
            .replace(/\'+/g, "") === currentTab
        ) {
          activeTab = props.tabs[i];
          tabsState = props.tabState.map((state, index) => {
            if (Number(i) === index) {
              return "active";
            }
            return "";
          });
        }
      }
      return Promise.resolve({
        activeTab,
        tabState: tabsState,
      });
    });
  }

  /**
   * Invoked when the windows address fragment changes.
   */
  onHashChange(): void {
    if (this.props !== undefined && this.props !== null) {
      FooterController.getDerivedStateFromProps(this.props, this.state).then(
        (derivedState: FooterControllerState) => {
          this.setState({ ...derivedState });
          Router.scrollTo();
        }
      );
    }
  }


  componentWillUnmount() {
    window.removeEventListener("hashchange", this.onHashChange);
  }
  /**
   * Invoked by the React framework immediately after a component is mounted (inserted into the tree).
   */
  componentDidMount(): void {
    window.addEventListener("hashchange", this.onHashChange, false);
    this.onHashChange();
  }

  /**
   * Returns a ReactNode containing the rendered component.
   *
   * @returns {React.ReactNode} The ReactNode containing the rendered component.
   */
  render(): React.ReactNode {
    return (
      <div className="footer-controller">
        {this.props.sectionName.toLocaleLowerCase() === "company" ? (
          <AboutUs />
        ) : null}
        {this.props.sectionName.toLocaleLowerCase() === "support" ? (
          <div className="tab-content">
            <div
              className={`tab-pane fade ${
                this.state.activeTab === "Contact us" ? " active show " : null
              }`}
              id="contact-us"
            >
              <ContactUs environment={this.props.relay.environment} />
            </div>
            <div
              className={`tab-pane fade ${
                this.state.activeTab === "Technical Issues"
                  ? " active show "
                  : null
              }`}
              id="technical-issues"
            >
              <TechnicalIssues />
            </div>
            <div
              className={`tab-pane fade ${
                this.state.activeTab === "FAQs" ? " active show " : null
              }`}
              id="faqs"
            >
              <FAQS />
            </div>
            <div
              className={`tab-pane fade ${
                this.state.activeTab === "Report an Issue"
                  ? " active show "
                  : null
              }`}
              id="report-an-issue"
            >
              <ReportBug environment={this.props.relay.environment} />
            </div>
          </div>
        ) : null}
        {this.props.sectionName.toLocaleLowerCase() === "legal" ? (
          <div className="tab-content">
            <div
              className={`tab-pane fade ${
                this.state.activeTab === "Privacy Policy"
                  ? " active show "
                  : null
              }`}
              id="privacy-policy"
            >
              <PrivacyPolicy />
            </div>
            <div
              className={`tab-pane fade ${
                this.state.activeTab === "Terms & Conditions"
                  ? " active show "
                  : null
              }`}
              id="terms-conditions"
            >
              <TermsAndConditions />
            </div>
          </div>
        ) : null}
      </div>
    );
  }
}
